import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import template from './rejectRateReport.tpl.html';
import {StateParams} from "../../common";
import {ICommonQueryingReport} from "./commonQueryingReport";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {LineEquipmentGroup} from "../../models/counts/lineCounts";
import {States} from "../../shared/config/constant.module";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import CountsServiceName, {CountsService} from "../../services/counts/counts.service";
import LineConfigsServiceName, {LineConfigsService} from "../../services/lineConfigs/lineConfigs.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import {apsEnums} from "../../shared/dto/aps.enums";

let requires = [NavigationServiceName, CountsServiceName, LineConfigsServiceName, LoaderServiceName];

interface RejectRateReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  equipmentTypeId: string;
  autoRun: boolean;
}

interface RejectRateReportController extends ICommonQueryingReport {
  timeZoneId: string;
  generateReport(): void;
  typeChanged(): void;
  updateShop(): void;
  equipmentType: apsViewModels.LineEquipmentNameItem;
  types: apsViewModels.LineEquipmentNameItem[];
  allTypes: apsViewModels.LineEquipmentNameItem[];
  group: LineEquipmentGroup;
  dataFound: boolean;
  reportOptions: {
    shop: string,
    equipment: string,
    startDate: string;
    endDate: string;
  };
}

export default angular.module('aps.controller.rejectRateReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.REJECT_RATE_REPORT,
      url: '/admin/reports/rejectRate?startDate&endDate&equipmentTypeId&autoRun&reportPlantId&reportShopId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        equipmentTypeId: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        reportShopId: {dynamic: true},
      },
      controller: 'RejectRateReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Reject Rate Report',
        authorizeAPSIOAdmin: true
      }
    });
  })
  .controller('RejectRateReportCtrl', rejectRateController)
  .name;

function rejectRateController(
  $state: angular.ui.IStateService,
  $stateParams: RejectRateReportParams,
  $q: angular.IQService,
  navigationService: NavigationService,
  countsService: CountsService,
  lineConfigsService: LineConfigsService,
  loaderService: LoaderService
): RejectRateReportController {

  let vm = {} as RejectRateReportController;
  let shopsDefer = $q.defer<apsViewModels.LineEquipmentNameItem[]>();
  let shopTypes = shopsDefer.promise;
  let shopTypesInitialized = false;

  function initialize() {
    vm.requirePlantSelection = true;
    vm.requireShopSelection = true;
    let now = moment().add(1, 'hours').startOf('hour');
    vm.uiEndDate = now.toDate();
    vm.uiStartDate = now.clone().subtract(1, 'days').toDate();

    let typesPromise = lineConfigsService.getLineEquipmentNames()
      .then(types => {
        vm.allTypes = _.sortBy(types
          .filter(t => t.type !== apsEnums.LineEquipmentType.Counter), 'name');
      });

    let navPromise = navigationService.getNavigation()
      .then((nav) => {
        vm.plants = nav.plants();
        vm.selectedPlant = nav.selectedPlant;
        vm.updatePlant();
        vm.selectedShop = nav.selectedShop;
        return updateShopPromise();
      });

    loaderService.loadingPromise($q.all([typesPromise, navPromise])
      .then(() => loadState($stateParams)));
  }

  function loadState(params: RejectRateReportParams): angular.IPromise<any> {
    let allowAutoRun = true;

    if (params.reportPlantId && params.reportShopId && params.reportShopId !== params.shopId) {
      let plant = _.find(vm.plants, { id: params.reportPlantId });
      if (plant) {
        let shop = _.find(plant.shops, { id: params.reportShopId });
        if (shop && shop !== vm.selectedShop) {
          vm.selectedPlant = plant;
          updatePlant();
          vm.selectedShop = shop;
          updateShopPromise();
        }
      }
    }

    if (params.startDate) {
      let startDate = moment(params.startDate);
      if (startDate.isValid()) {
        vm.startDate = startDate.toDate();
        vm.uiStartDate = DateUtilityService.timeZoneToLocal(startDate, vm.timeZoneId).toDate();
      }
    }

    if (params.endDate) {
      let endDate = moment(params.endDate);
      if (endDate.isValid()) {
        vm.endDate = endDate.toDate();
        vm.uiEndDate = DateUtilityService.timeZoneToLocal(endDate, vm.timeZoneId).toDate();
      }
    }

    if (params.equipmentTypeId) {
      let id = parseInt(params.equipmentTypeId, 10);
      shopTypes.then(shopTypes => {
        vm.equipmentType = _.find(shopTypes, { id: id });
      });
    }

    return shopTypes.then(() => {
      if (!vm.selectedPlant
        || !vm.selectedShop
        || !vm.equipmentType) {

        allowAutoRun = false;
      }

      if (allowAutoRun && params.autoRun) {
        return runReport();
      }

      return $q.when();
    });
  }

  function saveState() {
    let reportShopId = vm.selectedShop ? vm.selectedShop.id : undefined;
    if (reportShopId === $stateParams.shopId) {
      reportShopId = undefined;
    }
    let params: RejectRateReportParams = {
      plantId: $stateParams.plantId,
      shopId: $stateParams.shopId,
      userId: $stateParams.userId,
      reportPlantId: (reportShopId && vm.selectedPlant) ? vm.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      startDate: vm.startDate ? vm.startDate.toISOString() : undefined,
      endDate: vm.endDate ? vm.endDate.toISOString() : undefined,
      equipmentTypeId: vm.equipmentType ? vm.equipmentType.id.toString() : undefined,
      autoRun: true
    };

    $state.transitionTo($state.current.name, params);
  }

  function runReport(): angular.IPromise<any> {
    vm.group = null;
    vm.dataFound = false;

    let group: LineEquipmentGroup = {
      types: [vm.equipmentType.type],
      name: vm.equipmentType.name,
      inspectCount: null,
      equipment: null,
      groupUpdated: null,
      rejectCount: null,
      names: [vm.equipmentType.name],
      hasRejects: true,
      lineEquipmentNameIds: [vm.equipmentType.id],
      lineLineEquipmentTabs: null
    };

    return countsService.getGroupRejectCounts(vm.selectedShop.id,
      group, vm.startDate, vm.endDate).then(results => {
        group.rejects = results;
        vm.group = group;
        for (let id in results) {
          if (results[id].counts.inspectCounts.length > 0) {
            vm.dataFound = true;
            break;
          }
        }
        vm.reportOptions = {
          shop: vm.selectedShop.name,
          equipment: vm.equipmentType.name,
          startDate: formatTime(vm.startDate),
          endDate: formatTime(vm.endDate)
        };
      });
  }

  function generateReport(): void {
    if (vm.form.$invalid) {
      return;
    }

    saveState();

    loaderService.loadingPromise(runReport());
  }
  vm.generateReport = generateReport;

  function typeChanged(): void {
    if (vm.group && (!vm.equipmentType || vm.group.name !== vm.equipmentType.name)) {
      vm.group = null;
    }
  }
  vm.typeChanged = typeChanged;

  function updatePlant(): void {
    vm.timeZoneId = vm.selectedPlant
      ? vm.selectedPlant.timeZoneId : null;
    vm.selectedShop = null;
  }
  vm.updatePlant = updatePlant;

  function updateShopPromise(): angular.IPromise<any> {
    let nameIds: number[] = [];
    if (shopTypesInitialized) {
      shopsDefer = $q.defer();
      shopTypes = shopsDefer.promise;
    }
    return lineConfigsService.getLineConfiguration(vm.selectedShop.id)
      .then((result) => {
        result.lines.forEach(line => {
          line.equipment.forEach(eq => {
            nameIds.push(eq.lineEquipmentNameId);
          });
        });
        let shopNameIds = _.uniq(nameIds);
        vm.types = _.filter(vm.allTypes, (v) => {
          return shopNameIds.indexOf(v.id) > -1;
        });
        shopTypesInitialized = true;
        shopsDefer.resolve(vm.types);
      }, error => {
        shopTypesInitialized = true;
        shopsDefer.reject(error)
      });
  }

  function updateShop(): void {
    loaderService.loadingPromise(updateShopPromise());
  }

  vm.updateShop = updateShop;

  function formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, vm.timeZoneId);
  }

  initialize();
  return vm;
}



// WEBPACK FOOTER //
// ./src/app/controllers/reports/rejectRateReport.controller.ts